/**
 * Currency formatting utilities — single source of truth.
 *
 * Legacy tables store pounds as decimal(10,2). Modern tables store pence as integer.
 * Use the appropriate formatter depending on the source data unit.
 */

const GBP_FULL = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const GBP_COMPACT = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

// ============================================================
// CONVERSION HELPERS
// ============================================================

/** Convert pounds to pence for storage. */
export function toPence(pounds: number): number {
  return Math.round(pounds * 100)
}

/** Convert pence to pounds for display. */
export function fromPence(pence: number): number {
  return pence / 100
}

// ============================================================
// PENCE FORMATTERS (input: integer pence)
// ============================================================

/** Format pence as £ string with 2dp — e.g. 123456 → "£1,234.56" */
export function formatPence(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return '-'
  return GBP_FULL.format(pence / 100)
}

/** Format pence as compact £ string (0dp) — e.g. 123456 → "£1,235" */
export function formatPenceCompact(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return '-'
  return GBP_COMPACT.format(pence / 100)
}

// ============================================================
// POUNDS FORMATTERS (input: decimal pounds — legacy tables)
// ============================================================

/** Format pounds as £ string with 2dp — e.g. 1234.56 → "£1,234.56" */
export function formatPounds(pounds: number | null | undefined): string {
  if (pounds === null || pounds === undefined) return '-'
  return GBP_FULL.format(pounds)
}

/** Format pounds as compact £ string (0dp) — e.g. 1234.56 → "£1,235" */
export function formatPoundsCompact(pounds: number | null | undefined): string {
  if (pounds === null || pounds === undefined) return '-'
  return GBP_COMPACT.format(pounds)
}

// ============================================================
// BACKWARDS-COMPATIBLE ALIASES
// ============================================================

/**
 * Format pounds with 2dp.
 * @deprecated Prefer formatPounds() or formatPence() for clarity.
 */
export function formatCurrency(
  amount: number | null | undefined,
  includePoundSign = true
): string {
  if (amount === null || amount === undefined) return '-'

  const formatted = amount.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return includePoundSign ? `£${formatted}` : formatted
}

/**
 * Format pounds, hiding .00 decimals.
 * @deprecated Prefer formatPounds() or formatPoundsCompact() for clarity.
 */
export function formatCurrencyCompact(
  amount: number | null | undefined,
  includePoundSign = true
): string {
  if (amount === null || amount === undefined) return '-'

  const hasDecimals = amount % 1 !== 0

  const formatted = amount.toLocaleString('en-GB', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  })

  return includePoundSign ? `£${formatted}` : formatted
}

// ============================================================
// INPUT PARSING
// ============================================================

/**
 * Parse a user-entered pounds string to pence integer.
 * Handles formats like "1,500", "£1500", "1500.50".
 */
export function parsePoundsToPence(input: string | number): number | undefined {
  if (input === '' || input === null || input === undefined) return undefined
  const cleaned = String(input).replace(/[£$,\s]/g, '')
  const num = parseFloat(cleaned)
  if (isNaN(num) || num < 0) return undefined
  return Math.round(num * 100)
}

